import { useEffect, useState } from "react";
import { Card, Col, Row, Table } from "react-bootstrap";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import { mdiEye, mdiFileEditOutline, mdiTrashCanOutline } from "@mdi/js";
import { toast } from "react-toastify";
import { Fab, TextField } from "@mui/material";

import { endpointPrincipal } from "../../api/endPoints/endpointPrincipal/endpointPrincipal";

import Paginacao from "../../components/Formularios/Pagination";
import ContainerTitulo from "../../components/Formularios/Container/ContainerTitulo";
import InputSemBorda from "../../components/Formularios/Inputs/InputsSemBorda";
import { showConfirmationDialog } from "../../components/Formularios/Modal/ModalExcluir";
import { Button } from "../../components/Formularios/Buttons/Button";
import ModalLoading from "../../components/Formularios/Modal/ModalLoading";

// Interface genérica para os dados
interface ItemListagem {
  [key: string]: any;
}

// Props para configurar o componente de listagem
interface ListagemPadraoProps<T extends ItemListagem> {
  titulo: string;
  endpoint: string;
  colunas: {
    titulo: string;
    campo: keyof T | ((item: T) => React.ReactNode);
    alinhamento?: 'start' | 'center' | 'end';
    pesquisavel?: boolean;
  }[];
  campoId: keyof T;
  rotaBase: string;
  camposPesquisa: (keyof T)[];
  usaPaginacaoServidor?: boolean;
  mensagemConfirmacaoExclusao?: (item: T) => string;
  parametroExclusao?: (item: T) => string | number;
}

export function ListagemPadrao<T extends ItemListagem>({
  titulo,
  endpoint,
  colunas,
  campoId,
  rotaBase,
  camposPesquisa,
  usaPaginacaoServidor = false,
  mensagemConfirmacaoExclusao,
  parametroExclusao,
}: ListagemPadraoProps<T>) {
  const navigate = useNavigate();

  const [itens, setItens] = useState<T[] | null>(null);
  const [itensFiltrados, setItensFiltrados] = useState<T[] | null>(null);
  const [isCarregando, setIsCarregando] = useState(false);
  const [termosPesquisa, setTermosPesquisa] = useState<Record<string, string>>({});
  
  // Paginação
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const registrosPorPagina = 10;

  // Ações CRUD
  const excluirItem = async (item: T) => {
    const itemId = parametroExclusao ? parametroExclusao(item) : item[campoId];
    const mensagem = mensagemConfirmacaoExclusao 
      ? mensagemConfirmacaoExclusao(item)
      : `Tem certeza que deseja excluir este registro?`;
    
    await showConfirmationDialog(
      "Excluir Registro",
      mensagem,
      "question",
      "Excluir",
      "Cancelar"
    )
      .then((result) => {
        if (result.confirmed) {
          excluirItemPorId(itemId);
        }
      })
      .catch((erro) => {
        toast.error(
          `Erro ao excluir registro. Motivo: ${erro.response?.data?.message || erro.message}`
        );
      });
  };

  async function excluirItemPorId(id: number | string) {
    setIsCarregando(true);
    try {
      await endpointPrincipal.delete(`${endpoint}/${id}`);
      buscarDados();
      toast.success("Registro excluído com sucesso");
    } catch (error: any) {
      toast.error(`Erro ao excluir: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCarregando(false);
    }
  }

  async function buscarDados() {
    setIsCarregando(true);
    try {
      let url = endpoint;
      
      // Se usar paginação no servidor
      if (usaPaginacaoServidor) {
        url = `${endpoint}?page=${currentPage}&limit=${registrosPorPagina}`;
        
        // Adiciona os termos de pesquisa não vazios como parâmetros
        Object.entries(termosPesquisa).forEach(([campo, valor]) => {
          if (valor.trim()) {
            url += `&${campo}=${encodeURIComponent(valor.trim())}`;
          }
        });
      }
      
      const response = await endpointPrincipal.get(url);
      
      // Tratamento para diferentes formatos de resposta da API
      if (usaPaginacaoServidor && response.data.data) {
        setItens(response.data.data);
        setTotalPages(response.data.totalPages || 1);
        setTotalItems(response.data.total || 0);
      } else {
        setItens(response.data);
        
        // Filtragem no cliente se não usar paginação no servidor
        if (!usaPaginacaoServidor) {
          filtrarDados(response.data);
        }
      }
    } catch (error: any) {
      toast.error(`Erro ao buscar dados: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsCarregando(false);
    }
  }

  // Filtragem no cliente
  function filtrarDados(dados: T[]) {
    // Verifica se todos os campos de pesquisa estão vazios
    const temPesquisa = Object.values(termosPesquisa).some(termo => termo.trim() !== '');
    
    if (!temPesquisa) {
      setItensFiltrados(dados);
      setTotalPages(Math.ceil(dados.length / registrosPorPagina));
      setTotalItems(dados.length);
      return;
    }
    
    const filtrados = dados.filter(item => 
      // Verifica se o item corresponde a algum dos termos de pesquisa
      Object.entries(termosPesquisa).some(([campo, termoPesquisa]) => {
        if (!termoPesquisa.trim()) return false;
        
        const valor = item[campo as keyof T];
        return valor && String(valor).toLowerCase().includes(termoPesquisa.toLowerCase());
      })
    );
    
    setItensFiltrados(filtrados);
    setTotalPages(Math.ceil(filtrados.length / registrosPorPagina));
    setTotalItems(filtrados.length);
  }

  // Efeito para buscar dados quando os termos de pesquisa mudam
  useEffect(() => {
    if (usaPaginacaoServidor) {
      const timeoutId = setTimeout(() => {
        buscarDados();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      buscarDados();
    }
  }, [termosPesquisa, currentPage, usaPaginacaoServidor]);

  // Efeito para filtrar dados quando o termo de pesquisa muda (paginação no cliente)
  useEffect(() => {
    if (!usaPaginacaoServidor && itens) {
      filtrarDados(itens);
      setCurrentPage(1); // Volta para a primeira página ao pesquisar
    }
  }, [termosPesquisa, itens, usaPaginacaoServidor]);

  // Obter os itens da página atual (paginação no cliente)
  const itensPaginados = usaPaginacaoServidor 
    ? itens 
    : itensFiltrados?.slice(
        (currentPage - 1) * registrosPorPagina,
        currentPage * registrosPorPagina
      );

  // Renderização do valor da coluna
  const renderColunaValor = (item: T, coluna: typeof colunas[0]) => {
    if (typeof coluna.campo === 'function') {
      return coluna.campo(item);
    }
    return item[coluna.campo];
  };

  return (
    <>
      <div className="container-fluid mt-2">
        <div className="title-container d-flex justify-content-between align-items-center">
          <ContainerTitulo titulo={titulo} />
          <Fab
            size="small"
            aria-label="add"
            onClick={() => navigate(`${rotaBase}/novo`)}
            sx={{
              width: "36px",
              height: "36px",
              minHeight: "unset",
              color: "white",
              backgroundColor: "#246E37",
              "&:hover": {
                backgroundColor: "#1e5a2d",
              },
            }}
          >
            <AddIcon sx={{ fontSize: "20px" }} />
          </Fab>
        </div>
        <Card>
          <Card.Body>
            <Row>
              <Col xs={12} md={11}>
                <div className="d-flex flex-wrap my-2 gap-2">
                  {colunas
                    .filter(coluna => typeof coluna.campo === 'string' && camposPesquisa.includes(coluna.campo as keyof T))
                    .map((coluna, index) => (
                      <div key={index} style={{ flex: '1 1 200px', minWidth: '200px', maxWidth: '300px' }}>
                        <TextField
                          fullWidth
                          size="small"
                          id={`pesquisa-${String(coluna.campo)}`}
                          label={`Pesquisar por ${coluna.titulo}`}
                          variant="outlined"
                          value={termosPesquisa[String(coluna.campo)] || ''}
                          onChange={(e) => {
                            setTermosPesquisa(prev => ({
                              ...prev,
                              [String(coluna.campo)]: e.target.value
                            }));
                            if (usaPaginacaoServidor) {
                              setCurrentPage(1);
                            }
                          }}
                          sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                              "& fieldset": {
                                borderColor: "#ced4da",
                              },
                              "&:hover fieldset": {
                                borderColor: "#80bdff",
                              },
                              "&.Mui-focused fieldset": {
                                borderColor: "#80bdff",
                              },
                            },
                          }}
                          InputLabelProps={{
                            shrink: true,
                          }}
                        />
                      </div>
                    ))}
                </div>
              </Col>
            </Row>
            
            <Table responsive="lg" hover striped className="w-100">
              <thead className="table-cabecalho">
                <tr>
                  {colunas.map((coluna, index) => (
                    <th 
                      key={index} 
                      className={coluna.alinhamento ? `text-${coluna.alinhamento}` : ''}
                    >
                      {coluna.titulo}
                    </th>
                  ))}
                  <th className="text-end px-4">Ações</th>
                </tr>
              </thead>
              <tbody>
                {itensPaginados?.map((item) => (
                  <tr key={String(item[campoId])}>
                    {colunas.map((coluna, index) => (
                      <td 
                        key={index}
                        className={coluna.alinhamento ? `text-${coluna.alinhamento}` : ''}
                      >
                        {renderColunaValor(item, coluna)}
                      </td>
                    ))}
                    <td className="text-end">
                      <div className="d-flex justify-content-end gap-2">
                        <Button
                          onclick={() => navigate(`${rotaBase}/visu/${item[campoId]}`)}
                          icone={mdiEye}
                          type="button"
                          className="botaoTransparente"
                          title={"Visualizar"}
                        />
                        <Button
                          onclick={() => navigate(`${rotaBase}/editar/${item[campoId]}`)}
                          icone={mdiFileEditOutline}
                          type="button"
                          className="botaoTransparente"
                          title={"Editar"}
                        />
                        <Button
                          onclick={() => excluirItem(item)}
                          icone={mdiTrashCanOutline}
                          type="button"
                          className="botaoTransparente"
                          title={"Excluir"}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            
            {/* Paginação */}
            {totalItems > 0 && (
              <div className="mt-3">
                {usaPaginacaoServidor ? (
                  <div className="d-flex justify-content-center">
                    <nav>
                      <ul className="pagination">
                        <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage - 1)}
                            disabled={currentPage === 1}
                          >
                            Anterior
                          </button>
                        </li>
                        {[...Array(totalPages)].map((_, index) => (
                          <li 
                            key={index + 1} 
                            className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => setCurrentPage(index + 1)}
                            >
                              {index + 1}
                            </button>
                          </li>
                        ))}
                        <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                          <button 
                            className="page-link" 
                            onClick={() => setCurrentPage(currentPage + 1)}
                            disabled={currentPage === totalPages}
                          >
                            Próximo
                          </button>
                        </li>
                      </ul>
                    </nav>
                  </div>
                ) : (
                  <Paginacao
                    currentPage={currentPage}
                    totalPages={totalPages}
                    setCurrentPage={setCurrentPage}
                    dados={itensFiltrados || []}
                  />
                )}
              </div>
            )}
            
            {/* Mensagem quando não há registros */}
            {(!itensPaginados || itensPaginados.length === 0) && !isCarregando && (
              <div className="text-center py-4">
                <p className="text-muted">Nenhum registro encontrado</p>
              </div>
            )}
          </Card.Body>
        </Card>
        
        {/* Modal de carregamento */}
        <ModalLoading show={isCarregando} label="Carregando..." />
      </div>

      <style>
        {`
          .title-container {
            padding: 8px 0px;
            margin-bottom: 8px;
          }

          @media screen and (max-width: 412px) {
            .title-container {
              padding: 8px;
            }
          }

          /* Estilos para paginação do servidor */
          .pagination .page-item.active .page-link {
            background-color: #246E37 !important;
            border-color: #246E37 !important;
            color: white !important;
          }

          .pagination .page-item.active .page-link:hover {
            background-color: #1e5a2d !important;
            border-color: #1e5a2d !important;
            color: white !important;
          }

          .pagination .page-link:hover {
            background-color: #f8f9fa !important;
            border-color: #246E37 !important;
            color: #246E37 !important;
          }

          /* Estilos para componente Paginacao personalizado */
          .page-item.active .page-link {
            background-color: #246E37 !important;
            border-color: #246E37 !important;
            color: white !important;
          }

          .page-item.active .page-link:hover {
            background-color: #1e5a2d !important;
            border-color: #1e5a2d !important;
            color: white !important;
          }

          .page-link:hover {
            background-color: #f8f9fa !important;
            border-color: #246E37 !important;
            color: #246E37 !important;
          }

          /* Estilos adicionais para garantir aplicação */
          ul.pagination .page-item.active .page-link,
          nav .pagination .page-item.active .page-link {
            background-color: #246E37 !important;
            border-color: #246E37 !important;
            color: white !important;
          }
        `}
      </style>
    </>
  );
} 